import { FC, useMemo, useCallback, useEffect, useState, ReactElement } from 'react'
import { DefinedUseQueryResult } from '@tanstack/react-query'

import {
  CrudModal,
  DropdownButton,
  ISafetyCaseHazardMitigationValues,
  SafetyCaseHazardMitigationForm,
  safetyCaseHazardMitigationToSafetyCaseHazardMitigationValues,
  safetyCaseHazardMitigationValuesToSafetyCaseHazardMitigationAddUpsert,
  safetyCaseHazardMitigationValuesToSafetyCaseHazardMitigationUpdateUpsert,
} from '../../../components'
import SafetyCaseHazardMitigationsCardsList from './safety-case-hazard-mitigations-cards-list'
import CreateMitigationWorkOrderModal from './create-mitigation-work-order-modal'

import { useModal } from '../../../hooks'
import {
  useCreateSafetyCaseHazardMitigationMutation,
  useUpdateSafetyCaseHazardMitigationMutation,
  useDeleteSafetyCaseHazardMitigationMutation,
} from '../../../services'

import { ISafetyCaseHazard, ISafetyCaseHazardMitigation } from '../../../interfaces'

type SafetyCaseHazardMitigationsProps = {
  hasWriteAccess?: boolean
  hazardQuery: DefinedUseQueryResult<ISafetyCaseHazard | undefined, Error>
  setHeaderControls: (headerControls?: ReactElement) => void
  hazardMitigationsPagination: {
    pageSize: number
    pageIndex: number
  }
  setHazardMitigationsPagination: (values: { [key: string]: number }) => void
}

const SafetyCaseHazardMitigations: FC<SafetyCaseHazardMitigationsProps> = props => {
  const {
    hasWriteAccess,
    hazardQuery,
    setHeaderControls,
    hazardMitigationsPagination,
    setHazardMitigationsPagination,
  } = props

  const { data: hazard, isFetching: isHazardLoading, refetch: refetchHazard } = hazardQuery

  const mitigations = hazard?.mitigations

  const { isOpen: isMitigationModalOpen, closeModal: closeMitigationModal, openModal: openMitigationModal } = useModal()

  const [crudModalMode, setCrudModalMode] = useState<'create' | 'delete' | 'update'>()

  const handleOpenCreateMitigationModal = useCallback(() => {
    setCrudModalMode('create')
    openMitigationModal()
  }, [openMitigationModal])

  const [deletingMitigation, setDeletingMitigation] = useState<ISafetyCaseHazardMitigation>()

  const handleOpenDeleteMitigationModal = useCallback(
    (mitigation: ISafetyCaseHazardMitigation) => {
      setDeletingMitigation(mitigation)
      setCrudModalMode('delete')
      openMitigationModal()
    },
    [openMitigationModal],
  )

  const [updatingMitigation, setUpdatingMitigation] = useState<ISafetyCaseHazardMitigation>()

  const handleOpenUpdateMitigationModal = useCallback(
    (mitigation: ISafetyCaseHazardMitigation) => {
      setUpdatingMitigation(mitigation)
      setCrudModalMode('update')
      openMitigationModal()
    },
    [openMitigationModal],
  )

  const {
    isOpen: isMitigationWorkOrderModalOpen,
    closeModal: closeMitigationWorkOrderModal,
    openModal: openMitigationWorkOrderModal,
  } = useModal()

  const headerControls = useMemo(
    () => (
      <DropdownButton
        type="primary"
        iconClassName="bi-chevron-down ml-2"
        options={[
          { label: 'Add Mitigation', handler: () => handleOpenCreateMitigationModal() },
          { label: 'Add Work Order Mitigation', handler: () => openMitigationWorkOrderModal() },
        ]}
      >
        Add Mitigation
      </DropdownButton>
    ),
    [handleOpenCreateMitigationModal, openMitigationWorkOrderModal],
  )

  useEffect(() => {
    setHeaderControls(headerControls)
    return () => setHeaderControls(undefined)
  }, [setHeaderControls, headerControls])

  const mitigationActionsButtonOptions = useMemo(
    () =>
      hasWriteAccess
        ? [
            {
              label: 'Update',
              handler: (mitigation: ISafetyCaseHazardMitigation) => handleOpenUpdateMitigationModal(mitigation),
            },
            {
              label: 'Delete',
              handler: (mitigation: ISafetyCaseHazardMitigation) => handleOpenDeleteMitigationModal(mitigation),
            },
          ]
        : undefined,
    [hasWriteAccess, handleOpenUpdateMitigationModal, handleOpenDeleteMitigationModal],
  )

  if (!hazard) {
    return null
  }

  return (
    <>
      <CrudModal
        modalSettings={{
          isOpen: isMitigationModalOpen,
          closeModal: closeMitigationModal,
          mode: crudModalMode,
        }}
        createSettings={{
          title: 'Mitigation',
          useCreateMutation: useCreateSafetyCaseHazardMitigationMutation,
          formValuesToAddUpsert: (mitigationValues: ISafetyCaseHazardMitigationValues) =>
            safetyCaseHazardMitigationValuesToSafetyCaseHazardMitigationAddUpsert(mitigationValues, {
              hazardId: hazard.id,
            }),
          onCreateSuccessMessage: 'Mitigation successfully created',
          onCreateErrorMessage: 'Something went wrong while creating mitigation',
          onSuccessCallback: refetchHazard,
        }}
        updateSettings={{
          title: 'Mitigation',
          useUpdateMutation: useUpdateSafetyCaseHazardMitigationMutation,
          entityToFormValues: safetyCaseHazardMitigationToSafetyCaseHazardMitigationValues,
          formValuesToUpdateUpsert: (mitigationValues: ISafetyCaseHazardMitigationValues) =>
            safetyCaseHazardMitigationValuesToSafetyCaseHazardMitigationUpdateUpsert(
              mitigationValues,
              updatingMitigation,
            ),
          onUpdateSuccessMessage: 'Mitigation successfully updated',
          onUpdateErrorMessage: 'Something went wrong while updating mitigation',
          onSuccessCallback: refetchHazard,
          updatingItem: updatingMitigation,
        }}
        deleteSettings={{
          title: 'Delete Mitigation',
          message: deletingMitigation?.workOrders?.length
            ? `Are you sure you want to delete? This action cannot be undone. Associated Work Order${
                deletingMitigation.workOrders.length > 1 ? 's' : ''
              } ${new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(
                deletingMitigation.workOrders.map(workOrder => `#${workOrder.id}`),
              )} will be detached from this mitigation, but will still exist and must be manually deleted from the Work Orders page if needed.`
            : 'Are you sure you want to delete? This action cannot be undone.',
          useDeleteMutation: useDeleteSafetyCaseHazardMitigationMutation,
          onDeleteSuccessMessage: 'Mitigation successfully deleted',
          onDeleteErrorMessage: 'Something went wrong while deleting mitigation',
          onSuccessCallback: refetchHazard,
          deletingItem: deletingMitigation,
        }}
        FormComponent={SafetyCaseHazardMitigationForm}
        formName="safety-case-hazard-mitigation"
        mutators={undefined}
      />

      <CreateMitigationWorkOrderModal
        isOpen={isMitigationWorkOrderModalOpen}
        closeModal={closeMitigationWorkOrderModal}
        hazard={hazard}
        onSuccessCallback={refetchHazard}
      />

      <SafetyCaseHazardMitigationsCardsList
        mitigations={mitigations}
        pagination={hazardMitigationsPagination}
        setPagination={setHazardMitigationsPagination}
        actionsButtonOptions={mitigationActionsButtonOptions}
        isLoading={isHazardLoading}
      />
    </>
  )
}

export default SafetyCaseHazardMitigations
